<template>
  <Modal title="" :max-width="640" :header-border="false" body-overflow="auto" body-max-height="500px" :body-padding="false" @close="onClose">
    <template #header>
      <div class="cd__title-wrap">
        <span class="cd-source">
          <span class="cd-source__mark" :style="sourceStyle">
            <component :is="sourceIconComponent" />
          </span>
          {{ sourceLabel }}
        </span>
        <div class="cd__name">{{ titleDisplay }}</div>
        <div class="cd__sub">
          <template v-for="(seg, i) in subSegments" :key="i">
            <span v-if="i > 0" class="cd__dot" />
            <span>{{ seg }}</span>
          </template>
        </div>
      </div>
    </template>

    <div class="eng-body">
      <div class="cd-notes">
        <!-- Contact -->
        <div v-if="contactName" class="cd-note">
          <span class="cd-note__k">Contact</span>
          <span class="cd-note__v">{{ contactName }}</span>
        </div>

        <!-- Type-specific fields -->
        <div v-if="outcome" class="cd-note">
          <span class="cd-note__k">Outcome</span>
          <span class="cd-note__v">{{ outcome }}</span>
        </div>
        <div v-if="dueDate" class="cd-note">
          <span class="cd-note__k">Due</span>
          <span class="cd-note__v">{{ dueDate }}</span>
        </div>
        <div v-if="duration" class="cd-note">
          <span class="cd-note__k">Duration</span>
          <span class="cd-note__v">{{ duration }}</span>
        </div>
        <div v-if="locationDisplay" class="cd-note">
          <span class="cd-note__k">Location</span>
          <span class="cd-note__v">{{ locationDisplay }}</span>
        </div>
      </div>

      <!-- Body / notes -->
      <div v-if="bodyDisplay" class="eng-prose">{{ bodyDisplay }}</div>
    </div>

    <template #footer>
      <button class="cd-btn cd-btn--ghost" @click="onClose">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, h } from 'vue'
import Modal from '../../next/components/Modal.vue'

const props = defineProps<{
  /** Timeline item type e.g. hubspot_call, pipedrive_meeting, etc. */
  itemType: string
  /** Timeline item data payload */
  data: Record<string, any>
  /** Created-at timestamp from the timeline item */
  createdAt?: string
  onClose: () => void
}>()

// ── Source detection ────────────────────────────────────────────

type CrmSource = 'hubspot' | 'pipedrive'

const crmSource = computed<CrmSource>(() =>
  props.itemType.startsWith('pipedrive_') ? 'pipedrive' : 'hubspot'
)

const engagementType = computed(() => {
  const t = props.itemType
  // Extract the engagement kind from the type string
  if (t.includes('call')) return 'Call'
  if (t.includes('meeting')) return 'Meeting'
  if (t.includes('task')) return 'Task'
  if (t.includes('note')) return 'Note'
  if (t.includes('activity')) return 'Activity'
  return 'Engagement'
})

// ── Source badge ────────────────────────────────────────────────

const sourceLabel = computed(() => crmSource.value === 'pipedrive' ? 'Pipedrive' : 'HubSpot')
const sourceStyle = computed(() =>
  crmSource.value === 'pipedrive'
    ? { background: '#017737', color: '#fff' }
    : { background: '#ff7a59', color: '#fff' }
)

const hsIcon = () => h('svg', { viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.6' }, [
  h('circle', { cx: '6', cy: '10', r: '2.4' }),
  h('circle', { cx: '11.5', cy: '5.5', r: '1.6' }),
  h('path', { d: 'M7.4 8.4l3-1.8M11.5 7.1v2', 'stroke-linecap': 'round' }),
])
const pdIcon = () => h('svg', { viewBox: '0 0 16 16', fill: 'currentColor' }, [
  h('path', { d: 'M8 2a4 4 0 014 4v2.5a1.5 1.5 0 01-1.5 1.5h-5A1.5 1.5 0 014 8.5V6a4 4 0 014-4zm0 9c-1.3 0-2.4.8-2.8 2h5.6A3 3 0 008 11z' }),
])

const sourceIconComponent = computed(() => crmSource.value === 'pipedrive' ? pdIcon : hsIcon)

// ── Title ──────────────────────────────────────────────────────

const titleDisplay = computed(() => {
  const d = props.data
  return String(d?.clean_subject || d?.subject || engagementType.value).trim()
})

// ── Sub-segments ───────────────────────────────────────────────

const subSegments = computed(() => {
  const parts: string[] = [engagementType.value]
  const raw = props.createdAt || props.data?.created_at || props.data?.occurred_at
  if (raw) {
    try {
      const dt = new Date(raw)
      parts.push(dt.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }))
    } catch { /* skip */ }
  }
  // Pipedrive-specific: done status
  if (crmSource.value === 'pipedrive' && props.data?.done != null) {
    parts.push(props.data.done ? 'Done' : 'Open')
  }
  return parts
})

// ── Fields ─────────────────────────────────────────────────────

const contactName = computed(() => String(props.data?.contact_info?.name || '').trim())
const outcome = computed(() => String(props.data?.outcome || '').trim())
const dueDate = computed(() => String(props.data?.due_date || '').trim())
const duration = computed(() => String(props.data?.duration || '').trim())
const locationDisplay = computed(() => String(props.data?.location || '').trim())

const bodyDisplay = computed(() => {
  const d = props.data
  if (!d) return ''
  // Pipedrive: note field may contain HTML
  if (crmSource.value === 'pipedrive') {
    const note = String(d.note || d.public_description || '').replace(/<[^>]*>/g, '').trim()
    return note || String(d.body || '').trim()
  }
  return String(d.body || '').trim()
})
</script>

<style scoped>
.eng-body {
  display: flex;
  flex-direction: column;
  padding: var(--space-5);
}

.eng-prose {
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border-subtle);
  font-size: var(--fs-sm);
  line-height: var(--lh-loose);
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
